import React from 'react';
import { Paperclip } from 'lucide-react';
import { getProvinceName } from '../../utils/provinceNames';
import {
  SurveyAnswer,
  SurveyQuestion,
  buildAnswerMap,
  getOptionLabel,
  isQuestionVisible,
  vociTerritorio,
} from '../../utils/surveyAnswerFormat';

/**
 * Risposte inviate a un sondaggio, in sola lettura: intestazione (titolo, data
 * di invio, territorio del rispondente) e, per ogni step, le domande con la
 * risposta leggibile (etichette delle opzioni, elenchi, tabelle). Condiviso tra
 * area riservata (studi) e area associativa (rappresentanti): nessuna shell.
 */

export interface DomandaRisposte extends SurveyQuestion {
  order?: number | null;
  description?: string | null;
}

export interface StepRisposte {
  stepId: string;
  order?: number | null;
  title: string;
  description?: string | null;
  questions: DomandaRisposte[];
}

export interface SondaggioRisposte {
  id: string;
  title: string;
  description?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  settings?: { welcomeMessage?: string | null } | null;
  steps: StepRisposte[];
}

export interface RispostaInviata {
  id?: string;
  status: string;
  startedAt?: string | null;
  completedAt?: string | null;
  totalTimeSeconds?: number | null;
  respondent?: {
    nominativo?: string | null;
    partitaIVA?: string | null;
    /** Sondaggi per rappresentanti: «Nome Cognome» e carica principale (se il server li fornisce) */
    rappresentante?: string | null;
    carica?: string | null;
    territorio?: { livello: string; regioni?: string[] | null; province?: string[] | null } | null;
  } | null;
  answers: SurveyAnswer[];
}

interface RisposteInviateProps {
  survey: SondaggioRisposte;
  response: RispostaInviata;
  /** false: niente card bianche (la pagina mette già la sua cornice, es. area associativa) */
  cornice?: boolean;
}

const NESSUNA = '— nessuna risposta';

const perOrdine = <T extends { order?: number | null }>(elenco: T[]): T[] =>
  elenco
    .map((el, i) => ({ el, i }))
    .sort((a, b) => (a.el.order ?? a.i) - (b.el.order ?? b.i) || a.i - b.i)
    .map(({ el }) => el);

export const formatoDataOra = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return '';
  return data.toLocaleString('it-IT', {
    day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

const formatoData = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const data = new Date(iso);
  if (Number.isNaN(data.getTime())) return '';
  return data.toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' });
};

/** «Periodo: dal … al …» con le sole date presenti; nessuna data → ''. */
export const testoPeriodo = (inizio: string | null | undefined, fine: string | null | undefined): string => {
  const dal = formatoData(inizio);
  const al = formatoData(fine);
  if (dal && al) return `dal ${dal} al ${al}`;
  if (dal) return `dal ${dal}`;
  if (al) return `al ${al}`;
  return '';
};

/** Durata della compilazione: «1 h 5 min», «2 min 10 s», «45 s»; assente o non positiva → ''. */
export const testoDurata = (secondi: number | null | undefined): string => {
  if (secondi == null || !Number.isFinite(secondi) || secondi <= 0) return '';
  const totale = Math.round(secondi);
  const ore = Math.floor(totale / 3600);
  const minuti = Math.floor((totale % 3600) / 60);
  const sec = totale % 60;
  if (ore > 0) return minuti ? `${ore} h ${minuti} min` : `${ore} h`;
  if (minuti > 0) return sec ? `${minuti} min ${sec} s` : `${minuti} min`;
  return `${sec} s`;
};

const nomeProvincia = (sigla: string): string => {
  const nome = getProvinceName(sigla);
  return nome && nome !== sigla ? nome : '';
};

const valoriSeparati = (valore: string): string[] =>
  valore.split(',').map(v => v.trim()).filter(Boolean);

const parseMatrice = (valore: string): Record<string, string> => {
  try {
    const parsed = JSON.parse(valore);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      out[k] = Array.isArray(v) ? v.map(String).join(',') : String(v ?? '');
    }
    return out;
  } catch {
    return {};
  }
};

// File: link solo per indirizzi web o percorsi del server, altrimenti il nome del file.
// `//host/...` (indirizzo senza protocollo) resta testo: non è un percorso del server.
const hrefAllegato = (valore: string): string | null => {
  if (/^https?:\/\//i.test(valore)) return valore;
  if (valore.startsWith('/') && !valore.startsWith('//')) {
    const base = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/api\/?$/, '');
    return `${base}${valore}`;
  }
  return null;
};

const formatoDataSemplice = (valore: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(valore);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : valore;
};

const Vuota: React.FC = () => (
  <p className="text-sm italic text-gray-400 dark:text-gray-500">{NESSUNA}</p>
);

const RispostaDomanda: React.FC<{ domanda: DomandaRisposte; risposta: SurveyAnswer | undefined }> = ({ domanda, risposta }) => {
  if (domanda.type === 'territorio') {
    const voci = vociTerritorio(domanda, risposta);
    if (voci.length === 0) return <Vuota />;
    return (
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-600">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-700/60">
              <th scope="col" className="text-left px-3 py-2 text-xs font-medium text-gray-600 dark:text-gray-300">Provincia</th>
              <th scope="col" className="text-left px-3 py-2 text-xs font-medium text-gray-600 dark:text-gray-300">Risposta</th>
            </tr>
          </thead>
          <tbody>
            {voci.map(v => (
              <tr key={v.provincia} className="border-t border-gray-100 dark:border-gray-700">
                <td className="px-3 py-2 text-gray-800 dark:text-gray-200">
                  <span className="font-semibold">{v.provincia}</span>
                  {nomeProvincia(v.provincia) && <> {nomeProvincia(v.provincia)}</>}
                </td>
                <td className="px-3 py-2 text-gray-800 dark:text-gray-200">{v.risposta}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  const valore = (risposta?.value ?? '').toString();
  if (!valore.trim()) return <Vuota />;

  switch (domanda.type) {
    case 'radio':
    case 'dropdown':
      return <p className="text-sm text-gray-900 dark:text-gray-100">{getOptionLabel(domanda, valore)}</p>;

    case 'checkbox':
    case 'ranking': {
      const voci = valoriSeparati(valore).map(v => getOptionLabel(domanda, v));
      if (voci.length === 0) return <Vuota />;
      const Elenco = domanda.type === 'ranking' ? 'ol' : 'ul';
      return (
        <Elenco className={`${domanda.type === 'ranking' ? 'list-decimal' : 'list-disc'} pl-5 space-y-0.5 text-sm text-gray-900 dark:text-gray-100`}>
          {voci.map((v, i) => <li key={`${v}-${i}`}>{v}</li>)}
        </Elenco>
      );
    }

    case 'matrix':
    case 'matrix_multi': {
      const righe = domanda.matrixConfig?.rows || [];
      const colonne = domanda.matrixConfig?.columns || [];
      const scelte = parseMatrice(valore);
      if (righe.length === 0 || colonne.length === 0 || Object.keys(scelte).length === 0) return <Vuota />;
      const selezionata = (rowId: string, col: { colId: string; value?: string | null }) => {
        const valori = valoriSeparati(scelte[rowId] || '');
        return valori.includes(col.value ?? '') || valori.includes(col.colId);
      };
      return (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-600">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700/60">
                <th scope="col" className="px-3 py-2"><span className="sr-only">Voce</span></th>
                {colonne.map(c => (
                  <th key={c.colId} scope="col" className="px-3 py-2 text-center text-xs font-medium text-gray-600 dark:text-gray-300">{c.text}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {righe.map(r => (
                <tr key={r.rowId} className="border-t border-gray-100 dark:border-gray-700">
                  <th scope="row" className="px-3 py-2 text-left font-normal text-gray-800 dark:text-gray-200">{r.text}</th>
                  {colonne.map(c => (
                    <td key={c.colId} className="px-3 py-2 text-center text-gray-900 dark:text-gray-100">
                      {selezionata(r.rowId, c) ? (
                        <span role="img" aria-label={`${r.text}: ${c.text}`} className="font-semibold text-blue-600 dark:text-blue-400">✓</span>
                      ) : null}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    case 'file': {
      const href = hrefAllegato(valore);
      return href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-blue-600 dark:text-blue-400 hover:underline">
          <Paperclip className="w-3.5 h-3.5" aria-hidden="true" />
          Allegato
        </a>
      ) : (
        <p className="inline-flex items-center gap-1.5 text-sm text-gray-900 dark:text-gray-100">
          <Paperclip className="w-3.5 h-3.5 text-gray-500" aria-hidden="true" />
          {valore}
        </p>
      );
    }

    case 'signature':
      return /^data:image\//i.test(valore) ? (
        <img src={valore} alt="Firma" className="h-16 max-w-xs rounded border border-gray-200 dark:border-gray-600 bg-white" />
      ) : (
        <p className="text-sm text-gray-900 dark:text-gray-100">Firma acquisita</p>
      );

    case 'date':
      return <p className="text-sm text-gray-900 dark:text-gray-100">{formatoDataSemplice(valore)}</p>;

    case 'textarea':
      return <p className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-line">{valore}</p>;

    default:
      return <p className="text-sm text-gray-900 dark:text-gray-100 break-words">{valore}</p>;
  }
};

const RisposteInviate: React.FC<RisposteInviateProps> = ({ survey, response, cornice = true }) => {
  const mappa = buildAnswerMap(response.answers);
  const territorio = response.respondent?.territorio;
  const regionale = territorio?.livello === 'regionale';
  const elencoTerritorio = regionale
    ? (territorio?.regioni || [])
    : (territorio?.province || []).map(p => (nomeProvincia(p) ? `${nomeProvincia(p)} (${p})` : p));
  const inviato = formatoDataOra(response.completedAt);
  const durata = testoDurata(response.totalTimeSeconds);
  const periodo = testoPeriodo(survey.startDate, survey.endDate);
  const benvenuto = (survey.settings?.welcomeMessage || '').trim();
  const rispondente = response.respondent;
  const nominativo = (rispondente?.nominativo || '').trim();
  const partitaIVA = (rispondente?.partitaIVA || '').trim();
  const rappresentante = [rispondente?.rappresentante, rispondente?.carica]
    .map(v => (v || '').trim())
    .filter(Boolean)
    .join(' · ');

  const classeCard = cornice
    ? 'bg-white dark:bg-gray-800 rounded-lg shadow p-5'
    : 'border-b border-gray-100 dark:border-gray-700 pb-5 last:border-b-0 last:pb-0';

  const conRisposta = (q: DomandaRisposte): boolean => {
    const risposta = mappa.get(q.questionId);
    if (!risposta) return false;
    if (q.type === 'territorio') return vociTerritorio(q, risposta).length > 0;
    return !!(risposta.value ?? '').toString().trim();
  };

  const steps = perOrdine(survey.steps || [])
    .map(step => ({
      step,
      // Domanda nascosta dalla logica condizionale e senza risposta: non era stata mostrata
      domande: perOrdine(step.questions || []).filter(q =>
        q.type !== 'html' && (conRisposta(q) || isQuestionVisible(q, mappa))),
    }))
    .filter(({ domande }) => domande.length > 0);

  const totaleDomande = steps.reduce((n, { domande }) => n + domande.length, 0);
  const risposte = steps.reduce((n, { domande }) => n + domande.filter(conRisposta).length, 0);
  const riepilogo = [
    `${steps.length} step`,
    `${risposte} ${risposte === 1 ? 'domanda risposta' : 'domande risposte'} su ${totaleDomande}`,
  ].join(' · ');

  return (
    <div className={cornice ? 'space-y-4' : 'space-y-5'}>
      <div className={classeCard}>
        <h2 className="text-base font-semibold text-gray-900 dark:text-white">{survey.title}</h2>
        {survey.description && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 whitespace-pre-line">{survey.description}</p>
        )}
        {periodo && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Periodo: {periodo}</p>
        )}

        {(nominativo || partitaIVA || rappresentante) && (
          <div className="mt-3 rounded-lg bg-gray-50 dark:bg-gray-700/40 px-3 py-2" role="group" aria-label="Compilato da">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Compilato da</p>
            {(nominativo || partitaIVA) && (
              <p className="text-sm text-gray-900 dark:text-gray-100">
                {nominativo}
                {partitaIVA && (
                  <span className="text-gray-500 dark:text-gray-400">{nominativo ? ' · ' : ''}P.IVA {partitaIVA}</span>
                )}
              </p>
            )}
            {rappresentante && (
              <p className="text-sm text-gray-700 dark:text-gray-300">{rappresentante}</p>
            )}
          </div>
        )}

        {inviato && (
          <p className="text-sm text-green-700 dark:text-green-400 mt-3">
            Inviato il {inviato}{durata && <span className="text-gray-500 dark:text-gray-400"> · compilato in {durata}</span>}
          </p>
        )}
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{riepilogo}</p>
        {elencoTerritorio.length > 0 && (
          <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
            <span className="font-medium">{regionale ? 'Regione di competenza' : 'Provincia di competenza'}:</span>{' '}
            {elencoTerritorio.join(', ')}
          </p>
        )}
        {benvenuto && (
          <p className="mt-3 border-l-4 border-blue-200 dark:border-blue-800 pl-3 text-sm text-gray-600 dark:text-gray-400 whitespace-pre-line">
            {benvenuto}
          </p>
        )}
      </div>

      {steps.map(({ step, domande }) => (
        <section key={step.stepId} className={classeCard} aria-label={step.title || undefined}>
          {step.title && (
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{step.title}</h3>
          )}
          {step.description && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 whitespace-pre-line">{step.description}</p>
          )}
          <dl className={`space-y-4 ${step.title || step.description ? 'mt-3' : ''}`}>
            {domande.map(q => (
              <div key={q.questionId}>
                <dt className="mb-1">
                  <span className="block text-sm font-medium text-gray-700 dark:text-gray-300">{q.text}</span>
                  {q.description && (
                    <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5 whitespace-pre-line">{q.description}</span>
                  )}
                </dt>
                <dd>
                  <RispostaDomanda domanda={q} risposta={mappa.get(q.questionId)} />
                </dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
    </div>
  );
};

export default RisposteInviate;
